<template>
    <v-card
        class="ticket-card pa-3 mb-2"
        :class="{
            'ticket-card--closed': isTicketClosed,
            'ticket-card--dragging': dragging,
            'ticket-card--draggable': statusDragEnabled && !isTicketPendente
        }"
        outlined
        hover
        :draggable="statusDragEnabled && !isTicketClosed && !isTicketPendente"
        @dragstart="onDragStart"
        @dragend="onDragEnd"
        @click="$emit('click')"
    >
        <div class="d-flex flex-column align-stretch">
            <div class="d-flex align-center justify-space-between mb-1">
                <span class="ticket-key text-caption font-weight-medium primary--text">{{ ticket.cod_ticket || ticket.id_ticket }}</span>
                <v-spacer></v-spacer>
                <v-tooltip v-if="showPrint" bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            icon
                            x-small
                            class="flex-shrink-0 ml-2"
                            color="primary"
                            v-bind="attrs"
                            v-on="on"
                            @click.stop="$emit('print')"
                        >
                            <v-icon small>mdi-printer</v-icon>
                        </v-btn>
                    </template>
                    <span>{{ $t('ticket.print.printTicket') }}</span>
                </v-tooltip>
                <v-tooltip v-if="ticket.sla_faixa" bottom :disabled="!ticket.sla_prazo_extenso">
                    <template v-slot:activator="{ on, attrs }">
                        <v-chip
                            x-small
                            label
                            class="flex-shrink-0 ml-2"
                            :color="slaCor(ticket)"
                            :text-color="isTicketClosed ? undefined : 'white'"
                            :outlined="isTicketClosed"
                            v-bind="attrs"
                            v-on="on"
                        >
                            <v-icon left x-small>{{ slaVencido(ticket) ? 'mdi-timer-alert' : 'mdi-timer-sand' }}</v-icon>
                            {{ ticket.sla_faixa === 'ATRASADO' ? $t('ticket.sla.late') : $t('ticket.sla.option', { n: parseInt(ticket.sla_faixa, 10) }) }}
                        </v-chip>
                    </template>
                    <span>
                        {{ $t('ticket.sla.deadline', { d: ticket.sla_prazo_extenso }) }}
                        <template v-if="slaVencido(ticket)"> · {{ $t('ticket.sla.overdue') }}</template>
                    </span>
                </v-tooltip>
            </div>
            <div class="flex-grow-1 min-width-0">
                <div class="d-flex align-start gap-title-row">
                    <div class="ticket-card-title text-body-2 font-weight-medium flex-grow-1 min-width-0" :title="ticket.titulo">
                        {{ ticket.titulo || '–' }}
                    </div>
                    <v-tooltip v-if="hasNewComment" bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip
                                x-small
                                class="flex-shrink-0 ticket-card-new-comment"
                                color="primary"
                                text-color="white"
                                label
                                v-bind="attrs"
                                v-on="on"
                            >
                                <v-icon left x-small>mdi-comment-alert</v-icon>
                                {{ $t('ticket.comments.newCommentBadge') }}
                            </v-chip>
                        </template>
                        <span>{{ $t('ticket.comments.newCommentTooltip') }}</span>
                    </v-tooltip>
                    <v-chip
                        v-if="isTicketClosed"
                        x-small
                        class="flex-shrink-0"
                        color="grey"
                        text-color="white"
                        label
                    >
                        <v-icon left x-small>mdi-lock-check</v-icon>
                        {{ $t('ticket.finalized.label') }}
                    </v-chip>
                </div>
                <div class="text-caption grey--text mt-1">
                    {{ ticket.servico_extenso || ticket.tipo_atendimento_extenso || ticket.tipo_ticket_extenso || '–' }}
                </div>
                <div class="d-flex align-center mt-2 flex-wrap gap-x">
                    <span v-if="assigneesLine" class="text-caption">
                        <v-icon x-small left>mdi-account</v-icon>
                        {{ assigneesLine }}
                    </span>
                    <span v-if="ticket.dt_inclusao_extenso" class="text-caption ml-2">
                        <v-icon x-small left>mdi-calendar</v-icon>
                        {{ ticket.dt_inclusao_extenso }}
                    </span>
                    <span v-if="ticket.dt_finalizacao_extenso" class="text-caption ml-2">
                        <v-icon x-small left>mdi-calendar-check</v-icon>
                        {{ ticket.dt_finalizacao_extenso }}
                    </span>
                </div>
                <div v-if="precandidatosTotal > 0" class="d-flex align-center mt-1 flex-wrap gap-x">
                    <span class="text-caption">
                        <v-icon x-small left>mdi-account-group</v-icon>
                        {{ $t('ticket.card.precandidatosTotal', { n: precandidatosTotal }) }}
                    </span>
                    <v-chip v-if="precandidatosAtualizados > 0" x-small class="ml-2" color="success" text-color="white" label>
                        <v-icon left x-small>mdi-account-check</v-icon>
                        {{ $t('ticket.card.precandidatosAtualizados', { n: precandidatosAtualizados }) }}
                    </v-chip>
                </div>
            </div>
        </div>
    </v-card>
</template>

<script>
import { ticketAssigneesDisplayLabel } from '@/utils/ticketAssignees';
import { ticketEstaPendente } from '@/utils/ticketPendente';

/** Cor de cada faixa de SLA (Tickets.SQL_SLA_FAIXA); vencido sobrepõe a da faixa. */
const SLA_CORES = {
    '10H': 'success',
    '17H': 'success',
    '24H': 'warning',
    '48H': 'deep-orange',
    ATRASADO: 'error'
};

function slaVencido(t) {
    return !!t && (t.sla_vencido === true || Number(t.sla_vencido) === 1);
}

function slaCor(t) {
    if (!t) return 'grey';
    return slaVencido(t) ? 'error' : SLA_CORES[t.sla_faixa] || 'grey';
}

export default {
    name: 'TicketCard',
    props: {
        ticket: {
            type: Object,
            required: true
        },
        /** Allow dragging the card to another board column (parent handles drop + API). */
        statusDragEnabled: {
            type: Boolean,
            default: false
        },
        /** Mostra o ícone de impressora (o pai abre a pré-visualização de impressão). */
        showPrint: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return { dragging: false };
    },
    computed: {
        isTicketClosed() {
            const t = this.ticket;
            if (!t) return false;
            return t.finalizado === 1 || t.finalizado === '1' || t.finalizado_extenso === 'Sim';
        },
        /** Pendente não é arrastado: só passa para Aberto quando a triagem do card é salva. */
        isTicketPendente() {
            const t = this.ticket;
            return ticketEstaPendente(t);
        },
        hasNewComment() {
            const t = this.ticket;
            if (!t) return false;
            return t.tem_comentario_novo === true || t.tem_comentario_novo === 1 || t.tem_comentario_novo === '1';
        },
        precandidatosTotal() {
            return Number(this.ticket && this.ticket.precandidatos_total) || 0;
        },
        precandidatosAtualizados() {
            return Number(this.ticket && this.ticket.precandidatos_atualizados) || 0;
        },
        assigneesLine() {
            return ticketAssigneesDisplayLabel(this.ticket);
        }
    },
    methods: {
        slaCor,
        slaVencido,
        onDragStart(e) {
            if (!this.statusDragEnabled || this.isTicketClosed || this.isTicketPendente || !this.ticket || this.ticket.id_ticket == null) {
                e.preventDefault();
                return;
            }
            this.dragging = true;
            try {
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(this.ticket.id_ticket));
            } catch (err) { /* ignore */ }
        },
        onDragEnd() {
            this.dragging = false;
        }
    }
};
</script>

<style scoped>
.ticket-card {
    cursor: pointer;
}
.ticket-card--draggable {
    cursor: grab;
}
.ticket-card--draggable:active {
    cursor: grabbing;
}
.ticket-card--dragging {
    opacity: 0.55;
}
.ticket-card--closed {
    border-left: 3px solid rgba(0, 0, 0, 0.22);
}
.ticket-card-title {
    white-space: normal;
    word-break: break-word;
    overflow-wrap: anywhere;
}
.gap-title-row {
    gap: 0.5rem;
}
.ticket-key {
    flex-shrink: 0;
}
.min-width-0 {
    min-width: 0;
}
.gap-x > * + * {
    margin-left: 0.25rem;
}
</style>
